import React, { useContext, useEffect, useState } from 'react';
import { Button, FormControlLabel, TextField, Checkbox, Grid, Box, Typography, DialogContentText } from "@material-ui/core";
import md5 from 'md5';
import { ApiVersionInfo, ApiVersionName, ApiVersionSet, ResultObj, allFetch, allFetchNoToken, checkSign, getApiVersionJSON, getAPIPath, permisionURL } from './js/Domain';
import DocumentMeta from 'react-document-meta';
import { useHistory, useLocation } from 'react-router-dom';
import { CircleProgress } from './components/Overlay';
import ReactCardFlip from './components/ReactCardFlip';
import { get_ESD_indexedDB_factory } from './js/function';
import { IndexedDBClass } from './js/indexedDB';
import { ReactOutlineSelect } from './_AAA/components/ReactTextField';
import { HandleEvent } from './js/types';
import { loginState } from './js/permissionFunction';
import { MainPageDialogContext } from './js/MainPageDialogContext';

/** 登入主畫面 */
function Login() {
    const history = useHistory(),
        location = useLocation(),
        /** 日期號碼 */
        device_code = new Date().getFullYear() +
            ('0' + (new Date().getMonth() + 1)).slice(-2) +
            ('0' + new Date().getDate()).slice(-2) +
            ('0' + new Date().getHours()).slice(-2) +
            ('0' + new Date().getMinutes()).slice(-2) +
            ('0' + new Date().getSeconds()).slice(-2);

    const { responseMessage } = useContext(MainPageDialogContext)

    // 是否已跑完API
    const [isLoaded, setIsLoaded] = useState<boolean>(false)
    // 卡片是否轉向
    const [isFlipped, setIsFlipped] = useState<boolean>(false)
    // 是否記住登入狀態
    const [setCookie, setSetCookie] = useState<boolean>(false)

    // 正常登錄 輸入資料
    const [loginData, setLoginData] = useState<{
        userName: string,
        password: string,
    }>({
        userName: '',
        password: '',
    })

    // 忘記密碼 輸入資料
    const [forgetPasswordData, setForgetPasswordData] = useState<{
        userName: string,
        email: string,
    }>({
        userName: '',
        email: '',
    })

    // 錯誤訊息
    const [errorMessages, setErrorMessages] = useState<{
        username: string | null,
        password: string | null,
        email: string | null,
    }>({
        username: null,
        password: null,
        email: null,
    })

    const [apiVersions, setApiVersions] = useState<ApiVersionSet | null>(null);
    const [currApiVersion, setCurrApiVersion] = useState<ApiVersionName | undefined>(undefined);

    // #region function
    /** 初始檢查登入狀態 */
    async function init() {
        localStorage.clear();
        sessionStorage.clear();

        const queryString = window.location.search;

        const urlParams = new URLSearchParams(queryString);
        //獲取携帶在param上的error
        const errorMessage = urlParams.get('error');

        //如果有發現error的話就顯示提示
        if (errorMessage) {
            responseMessage({ success: false, message: errorMessage, data: null })
        }

        const resApiVersions: ApiVersionSet | null = await getApiVersionJSON();
        setApiVersions(resApiVersions);
        setCurrApiVersion(resApiVersions ? resApiVersions.defaultApiVersion : undefined);

        await get_ESD_indexedDB_factory().then(async (idb: (IndexedDBClass | null)) => {
            //清除idb的ApiVersion的ObjectStore
            idb?.clear("ApiVersion")
        })
        // 檢查 localStorage 是否已有登入資料
        if (localStorage.setCookie === true || localStorage.setCookie === "true" || sessionStorage.company_code) {
            setSetCookie(true)
            // 檢查登入狀態
            checkSign().then(async res => {
                if (res.success) {
                    sessionStorage.company_code = res.data.company_code;
                    if (res.success) {
                        sessionStorage.company_code = res.data.company_code;
                        history.push('/Index/Home');
                    }
                }
            });
        }
        // 檢查是否已有 login_account
        if (localStorage.login_account) {
            setLoginData(prev => ({
                ...prev,
                userName: localStorage.login_account
            }))
        }
        setIsLoaded(true)
    }

    // #endregion

    // #region click function
    /** 點擊轉動畫面
     * @description 轉動同時清除警語資料
     */
    function toggleFilpped() {
        setErrorMessages({
            username: null,
            password: null,
            email: null,
        })
        setIsFlipped(prev => !prev)
    }

    /** 鍵盤監聽 */
    function onKeyDownEnter(e: React.KeyboardEvent<HTMLDivElement>) {
        if (e.key === 'Enter') {
            setIsLoaded(false)
            loginClick();
        }
    }

    /** input 事件 */
    function handleInput(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const status = e.target.name.split('--')[0],
            name = e.target.name.split('--')[1],
            value = e.target.value || ''

        if (status === 'login') {
            setLoginData(prev => ({
                ...prev,
                [name]: value
            }))
        } else if (status === 'forgetPassword') {
            setForgetPasswordData(prev => ({
                ...prev,
                [name]: value
            }))
        } else {
            console.error('input status error', status)
        }
    }

    /** 登入 Click */
    async function loginClick() {
        // 檢查是否皆填寫
        if (loginData.password && loginData.userName) {
            //清除警語
            setErrorMessages(prev => ({
                email: null,
                username: null,
                password: null,
            }))

            const uri = currApiVersion === ApiVersionName.DOT_NET_6_AM_N ? 'EnvironmentDataAggregation/Signin' : 'usercontrol/signin';
            let apiVersionInfo: ApiVersionInfo | undefined = undefined

            if (apiVersions && currApiVersion && apiVersions.apiDetails) {
                apiVersionInfo = {
                    iisApplicationName: apiVersions.apiDetails[currApiVersion].iisApplicationName,
                    version: currApiVersion
                }
            }

            await allFetchNoToken(uri, {
                username: loginData.userName,
                password: md5(loginData.password),
                device_type: 'WEB',
                device_code: localStorage.device_code ? localStorage.device_code : device_code
            }, apiVersionInfo
            ).then(async (res: ResultObj<{
                Plant: string,
                check_code: string,
                company_code: string,
                company_name: string,
                home_page: string,
                home_page_name: string,
                name: string,
                token: string,
                user_id: string,
                ds_id: string,
            }>) => {
                if (res.success) {
                    const companyCode = (res.data as any).company_code ?? '';
                    const companyCodeHead =
                        (res.data as any).company_code_head ??
                        (res.data as any).companyCodeHead ??
                        companyCode;
                    const isDealer = !!companyCode && !!companyCodeHead
                        ? companyCodeHead !== companyCode
                        : false;

                    // 成功登入後將資料設定進 localstorage 與 session
                    localStorage.device_code = device_code;
                    localStorage.token = res.data.token;
                    localStorage.login_account = loginData.userName;
                    localStorage.user_id = res.data.user_id;
                    localStorage.name = res.data.name;
                    localStorage.setCookie = setCookie;
                    localStorage.ds_id = res.data.ds_id || '';
                    localStorage.company_code = companyCode;
                    localStorage.company_code_head = companyCodeHead;
                    localStorage.isDealer = isDealer;

                    sessionStorage.Plant = res.data.Plant ?
                        res.data.Plant :
                        'plant' in res.data ?
                            res.data.plant :
                            "";
                    sessionStorage.company_code = companyCode;
                    sessionStorage.company_code_head = companyCodeHead;
                    // sessionStorage.company_code_head = 'KT';

                    sessionStorage.isDealer = isDealer;

                    sessionStorage.company_name = res.data.company_name;
                    sessionStorage.setCookie = setCookie;
                    sessionStorage.ds_id = res.data.ds_id || '';

                    const params = new URLSearchParams(location.search);
                    const paramHomePage = params.get('home_page');

                    //使用外部連結時，必須要携帶home_page參數，不然網站會沒法知道要載入哪一個路徑的頁面檔案
                    if (paramHomePage) {
                        localStorage.setItem('home_page', paramHomePage);
                        sessionStorage.setItem('home_page', paramHomePage);
                    }
                    const apiPath = await getAPIPath()
                    //是否有權限，無權限時只跳出提示，不進行頁面轉導
                    let hasPermission = true;
                    await fetch(`${apiPath}usercontrol/check`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.token}` }, body: JSON.stringify({}) })
                        .then(async (resUserControl) => {
                            const userControl: ResultObj<loginState> = await resUserControl.json();
                            if (userControl.success) {
                                let { path } = { ...userControl.data };
                                let response: Array<any> = await fetch(`${permisionURL}/_${sessionStorage.company_code}/Users/${path}/permission.json?x=` + Date.now()).then(r => r.json());

                                if (response.length > 0) {
                                    //如果使用者沒有設定的homepage的權限，則帶入nav中第一個擁有權限的homepage
                                    const targetObj = response.find(item => {
                                        return item.children.some((childrenItem: { name: string; }) => childrenItem.name === 'nav')
                                    })
                                    if (!targetObj || !targetObj.children.length) {
                                        hasPermission = false;
                                        alert("無權限使用");
                                    } else {
                                        const navList = targetObj.children.find((item: { name: string; }) => item.name === 'nav').children
                                        if (!navList.some((i: any) => i.code === res.data.home_page)) {
                                            localStorage.home_page = navList[0].code;
                                            sessionStorage.home_page = navList[0].code;
                                        } else {
                                            localStorage.home_page = res.data.home_page;
                                            sessionStorage.home_page = res.data.home_page;
                                        }
                                    }
                                } else {
                                    //logout
                                    hasPermission = false;
                                    alert("無權限使用");
                                }
                            } else {
                                hasPermission = false;
                                alert("無權限使用");
                            }
                        });
                    if (hasPermission) {
                        let redirect = decodeURIComponent(location.search.replace('?redirect=', ''));
                        const qIndex = redirect.indexOf('?');
                        //去除error訊息
                        if (qIndex !== -1) {
                            const path = redirect.slice(0, qIndex);
                            const query = redirect.slice(qIndex + 1);
                            const params = new URLSearchParams(query);
                            params.delete('error');
                            const newQuery = params.toString();
                            redirect = newQuery ? `${path}?${newQuery}` : path;
                        }
                        history.push(redirect || '/Index/Home');
                    }
                } else {
                    // 將回傳錯誤訊息設定
                    if (res.data && Object.getPrototypeOf(res.data).constructor.name === "Object") {
                        setErrorMessages(prev => ({
                            ...prev,
                            ...res.data
                        }))
                    }
                    // 清除密碼
                    setLoginData(prev => ({
                        ...prev,
                        password: ''
                    }))
                    alert(res.message);
                }
                setIsLoaded(true)
            });
        } else {
            setErrorMessages(prev => ({
                ...prev,
                username: loginData.userName ? null : '請入帳號',
                password: loginData.password ? null : '請入密碼',
            }))
        }
    }

    /** 忘記密碼 Click */
    async function forgetPasswordClick() {
        // 檢查是否皆填寫
        if (forgetPasswordData.email && forgetPasswordData.userName) {
            //清除警語
            setErrorMessages(prev => ({
                email: null,
                username: null,
                password: null,
            }))

            await allFetch('usercontrol/forgetpassword', {
                username: forgetPasswordData.userName,
                email: forgetPasswordData.email
            }).then(async res => {
                if (res.success) {
                    alert('已將新密碼寄至您的信箱。');
                    // 自動轉回登入畫面
                    toggleFilpped()
                } else {
                    // 將回傳錯誤訊息設定
                    if (res.data && Object.getPrototypeOf(res.data).constructor.name === "Object") {
                        setErrorMessages(prev => ({
                            ...prev,
                            ...res.data
                        }))
                    }
                    alert(res.message);
                }
                setIsLoaded(true)
            });
        } else {
            setErrorMessages(prev => ({
                ...prev,
                username: forgetPasswordData.userName ? null : '請入帳號',
                email: forgetPasswordData.email ? null : '請入信箱',
            }))
        }
    }
    // #endregion

    // #region useEffect
    useEffect(() => {
        init()
    }, [])

    // #endregion

    const meta = {
        meta: {
            // ex: name 標籤名稱, obj裡 google 次要標籤名稱, notranslate 次要標籤值
            name: {
                google: 'notranslate',
                viewport: "user-scalable=no, maximum-scale=1.0, width=device-width, initial-scale=1.0"
            }
        },
        title: "ESD - 登入" + (window.location.host.indexOf("test") > -1 ? "(測試)" : (window.location.host.indexOf("localhost") > -1 ? "(本機)" : ""))
    }

    if (!!isLoaded) {
        return <React.Fragment>
            <Grid component="main" className="middle">
                <DocumentMeta {...meta} />
                <ReactCardFlip isFlipped={isFlipped} flipDirection="horizontal" containerStyle={{ alignItems: 'center' }}>
                    {/* 正常登入 */}
                    <Box display="flex" flexWrap="wrap" className="cardRadius login-card" minWidth={{ xs: '100%', md: '900px', lg: '900px' }} style={{ height: "100%", overflow: "hidden", minHeight: '455px' }}>
                        <Grid item xs={false} md={6} lg={6} className="middle loginCard_Contact">
                            <ContactUs title={"聯絡我們"} text="有任何疑問嗎？歡迎與我們聯繫" />
                        </Grid>
                        <Grid item xs={12} md={6} lg={6} style={{ padding: "30px", background: "#fcfeff" }}>
                            <Typography component="h1" variant="h5">登入</Typography>
                            <form style={{ marginTop: "8px" }} noValidate>
                                <TextField
                                    name='login--userName'
                                    onChange={(e) => handleInput(e)}
                                    value={loginData.userName}
                                    type='text'
                                    placeholder='請輸入帳號'
                                    helperText={errorMessages.username}
                                    error={!!errorMessages.username}
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="使用者帳號"
                                    autoFocus
                                />
                                <TextField
                                    name='login--password'
                                    onChange={(e) => handleInput(e)}
                                    value={loginData.password}
                                    type='password'
                                    placeholder='請輸入密碼'
                                    helperText={errorMessages.password}
                                    error={!!errorMessages.password}
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="密碼"
                                    autoFocus
                                    onKeyDown={(e) => onKeyDownEnter(e)}
                                />
                                <FormControlLabel
                                    control={<Checkbox
                                        color="primary"
                                        id="setCookie"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setSetCookie(prev => !!e.target?.checked) }}
                                        className='custom-control-input'
                                        checked={!!setCookie}
                                    />}
                                    name='setCookie' label="記住我的登入狀態" />

                                <Button fullWidth variant="contained" onClick={loginClick} color="primary" className="MuiButton" style={{ margin: "8px 0px 16px", }}>
                                    登入
                                </Button>

                                <Grid container style={{ margin: "12px 0px" }}>
                                    <Grid item xs>
                                        <a role="button" tabIndex={0} href='#' onClick={toggleFilpped}
                                            rel="noreferrer noopener" >
                                            忘記密碼 ?
                                        </a>
                                    </Grid>
                                </Grid>
                            </form>
                            <Box mt={3}>
                                <Copyright />
                            </Box>
                        </Grid>
                        {
                            (process.env.REACT_APP_ENV === 'development' || process.env.REACT_APP_ENV === 'uat') && apiVersions && apiVersions.apiDetails &&
                            <ReactOutlineSelect
                                color="primary"
                                id="search--apiVersion"
                                name="search--apiVersion"
                                placeholder="請選擇"
                                optionKey="value"
                                changeEvent={(e: HandleEvent) => { setCurrApiVersion(e.target.value.value) }}
                                value={{ value: currApiVersion, name: currApiVersion }}
                                codeList={Object.keys(apiVersions.apiDetails).map(key => ({ value: key, name: key }))}
                                label="API環境" />
                        }
                    </Box>

                    {/* 忘記密碼 */}
                    <Box display="flex" flexWrap="wrap" className="cardRadius login-card" minWidth={{ xs: '100%', md: '900px', lg: '900px' }} style={{ height: "100%", overflow: "hidden", minHeight: '455px' }}>
                        <Grid item xs={12} md={6} lg={6} style={{ padding: "30px", background: "#fcfeff" }}>
                            <Typography component="h1" variant="h5">忘記密碼</Typography>
                            <DialogContentText style={{ marginTop: '1rem' }}>
                                請輸入帳號與註冊時綁定之E-mail，我們將為您產生新密碼寄至您的信箱
                            </DialogContentText>
                            <form style={{ marginTop: "8px" }} noValidate>

                                <TextField
                                    name='forgetPassword--userName'
                                    onChange={(e) => handleInput(e)}
                                    value={forgetPasswordData.userName}
                                    type='text'
                                    placeholder='請輸入帳號'
                                    helperText={errorMessages.username}
                                    error={!!errorMessages.username}
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="使用者帳號"
                                    autoFocus
                                />
                                <TextField
                                    name='forgetPassword--email'
                                    onChange={(e) => handleInput(e)}
                                    value={forgetPasswordData.email}
                                    type='text'
                                    placeholder='請輸入信箱'
                                    helperText={errorMessages.email}
                                    error={!!errorMessages.email}
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="信箱"
                                    autoFocus
                                />
                                <Button fullWidth variant="contained" onClick={forgetPasswordClick} color="primary" className="MuiButton" style={{ margin: "8px 0px 16px" }}>
                                    寄送新密碼
                                </Button>
                                <Grid container style={{ margin: "12px 0px" }}>
                                    <Grid item xs>
                                        <a role="button" tabIndex={0} href='#' onClick={toggleFilpped} rel="noreferrer noopener" >
                                            再次登入
                                        </a>
                                    </Grid>
                                </Grid>
                            </form>
                        </Grid>
                        <Grid item xs={false} md={6} lg={6} style={{ backgroundColor: "#5b6fac" }} className="middle">
                            <ContactUs title={"聯絡我們"} text="還是無法登入嗎？請與我們聯繫" />
                        </Grid>
                    </Box>

                </ReactCardFlip >
            </Grid >
        </React.Fragment >
    } else {
        return <CircleProgress />
    }
}

/** 版權 */
function Copyright() {
    return (
        <Typography variant="body2" color="textSecondary" align="center">
            {"Copyright © "}{new Date().getFullYear()}<br />
            <a target="_blank" role="button" tabIndex={0} rel="noreferrer noopener" color="inherit" href="https://www.genesys-tech.com">
                Genesys Technology Ltd.
            </a>{" "} All rights reserved.
        </Typography>
    );
}

/** 聯絡我們 */
function ContactUs(props: {
    /** 主題 */
    title: string,
    /** 詳細說明 */
    text: string
}) {
    return (
        <Box display={{ xs: "none", md: "block", lg: "block" }} textAlign={'center'}>
            <div className='contactUs_title'>{props.title}</div><br />
            <div className='contactUs_content'>{props.text}</div><br />
            <Button className="MuiOtherButton contactUs_Btn">
                <a target="_blank" role="button" tabIndex={0} rel="noreferrer noopener" href="https://www.genesys-tech.com">進入官網</a>
            </Button>
        </Box >
    );
}

export default Login;
